import { difference, fromPairs, intersection, pull } from 'lodash';
import { isEmpty } from 'lodash/fp';
import { DeepReadonly } from 'utility-types';

import { anyVaccinationStatusId, populationMeasureId } from '../config';
import _ from '../locale';
import { getAgeGroupsRange } from '../models/age-group';
import { CHARTS_NON_SUPPORTED_DATA_TYPES, TYPE_SCATTER_MAP_CHART } from '../models/chart-type';
import {
    AGE_GROUP_ID_KEY,
    ANTIBIOTIC_CLASS_ID_KEY,
    APPARENT_ID_KEY,
    CAUSE_ID_KEY,
    COMORBID_CAUSE_ID_KEY,
    COUNTERFACTUAL_ID_KEY,
    COVARIATE_ID_KEY,
    CRO_ID_KEY,
    DATA_TYPE_KEY,
    EDUCATION_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    GENDER_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    LOCATION_ID_KEY,
    LOWER_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    OUTCOME_ID_KEY,
    PATHOGEN_ID_KEY,
    PAYER_ID_KEY,
    PRIMARY_ENTITY_ID_KEY,
    PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    RACE_ID_KEY,
    REI_ID_KEY,
    RESPONSE_ID_KEY,
    RISK_EXPOSURE_ID_KEY,
    ROUND_ID_KEY,
    SDOH_ID_KEY,
    SEQUELA_ID_KEY,
    SEVERITY_ID_KEY,
    SYMPTOMATIC_ID_KEY,
    TOC_ID_KEY,
    UNDERLYING_CONDITION_KEY,
    UPPER_KEY,
    VACCINATION_STATUS_ID_KEY,
    getTimeUnitKeys,
} from '../models/data-key';
import {
    AntimicrobialResistanceRecords,
    CauseAssociatedComorbidityStudyCoefficients,
    CauseOutcome,
    Covid19Records,
    CroRecords,
    HealthThreatsMetricsRecords,
    ImpairmentRecords,
    InjuryRecords,
    LifeExpectancyRecords,
    PopulationRecords,
    RiskExposureRecords,
    SequelaOutcome,
    SevRecords,
    TakedaDengueRecords,
    areComorbidityCoprevalenceDataTypes,
    areComorbidityDataTypes,
    areReiDataTypes,
    dataTypeHasPrimaryEntity,
    getDataTypeLocalePrefix,
    getDataTypePrimaryEntityKey,
} from '../models/data-type';
import {
    AgeGroupEntity,
    ChartProviderFilterValue,
    ChartProviderFilters,
    ChartType,
    DataCondition,
    DataFilters,
    DataGranularity,
    DataGranularityKey,
    DataKey,
    DataRecord,
    DataResponse,
    DataType,
    TimeUnitKey,
} from '../types';
import getTimeUnitSorter from './data/time-units/get-time-unit-sorter';
import sortDataResponseByTimeUnit from './data/time-units/sort-data-response-by-time-unit';
import { formatValue } from './filters-helpers';

export const filterForecastingData = (
    response: DataResponse,
    pastEstimateScenarioId: number
): DataResponse => {
    const { columns, records } = response;
    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);

    return {
        columns,
        records: records.filter((record) => record[forecastScenarioIdx] === pastEstimateScenarioId),
    };
};

export const splitForecastData = (
    response: DataResponse,
    observedForecastScenarioId: number
): { observedData: DataResponse; forecastData: DataResponse } => {
    const { columns, records } = response;

    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);

    const observedRecords = records.filter(
        (record) => record[forecastScenarioIdx] === observedForecastScenarioId
    );
    const forecastRecords = records.filter(
        (record) => record[forecastScenarioIdx] !== observedForecastScenarioId
    );

    const observedData = { columns, records: observedRecords };

    const latestObservedRecord = observedRecords[observedRecords.length - 1];
    const forecastScenarioIds = new Set(
        forecastRecords.map((record) => record[forecastScenarioIdx])
    );
    const latestObservedRecords = latestObservedRecord
        ? [...forecastScenarioIds].map((forecastScenarioId) => {
              const record = [...latestObservedRecord];
              record[forecastScenarioIdx] = forecastScenarioId;
              return record;
          })
        : [];

    const forecastData = {
        columns,
        records: [...latestObservedRecords, ...forecastRecords],
    };

    return { observedData, forecastData };
};

export const connectPastEstimateWithForecasts = (
    { columns, records }: DataResponse,
    pastEstimateScenarioId: number,
    timeUnitKey: TimeUnitKey,
    multipleFilterKeys: DataGranularityKey[]
): DataResponse => {
    const timeUnitIdx = columns.indexOf(timeUnitKey);
    const forecastScenarioIdx = columns.indexOf(FORECAST_SCENARIO_ID_KEY);
    const upperIdx = columns.indexOf(UPPER_KEY);
    const lowerIdx = columns.indexOf(LOWER_KEY);
    const compareTimeUnits = getTimeUnitSorter(timeUnitKey);

    const filterRecordsByValue = (
        source: DataRecord[],
        columnIdx: number,
        value: number
    ): DataRecord[] => source.filter((record) => record[columnIdx] === value);

    const pastEstimateRecords = filterRecordsByValue(
        records,
        forecastScenarioIdx,
        pastEstimateScenarioId
    );
    const forecastRecords = records.filter(
        (record) => record[forecastScenarioIdx] !== pastEstimateScenarioId
    );

    if (isEmpty(pastEstimateRecords) || isEmpty(forecastRecords)) {
        // nothing to connect
        return { columns, records };
    }

    const keysToCompare = multipleFilterKeys.filter((key) => key !== FORECAST_SCENARIO_ID_KEY);
    const historicalLineKeyIdxs = keysToCompare.map((key) => columns.indexOf(key));
    const forecastScenarioLineKeyIdxs = multipleFilterKeys.map((key) => columns.indexOf(key));
    const getRecordKey = (record: DataRecord, idxs: number[]) =>
        JSON.stringify(idxs.map((idx) => record[idx]));
    const getHistoricalLineKey = (record: DataRecord) =>
        getRecordKey(record, historicalLineKeyIdxs);
    const getForecastScenarioLineKey = (record: DataRecord) =>
        getRecordKey(record, forecastScenarioLineKeyIdxs);

    // Historical (past estimate) records grouped by line.
    const pastEstimateRecordsByLine: Record<string, DataRecord[]> = {};
    for (const record of pastEstimateRecords) {
        const lineKey = getHistoricalLineKey(record);
        if (!pastEstimateRecordsByLine[lineKey]) {
            pastEstimateRecordsByLine[lineKey] = [];
        }
        pastEstimateRecordsByLine[lineKey].push(record);
    }

    // The first forecast point of each line + forecast scenario. Computed per line
    // because different conditions can have different forecast start years (e.g.
    // GBD2023 Cause forecasts start in 2026 while Sequela forecasts start in 2025);
    // a single global boundary would leave the later-starting lines disconnected.
    const firstForecastRecordByGroup: Record<string, DataRecord> = {};
    for (const record of forecastRecords) {
        const groupKey = getForecastScenarioLineKey(record);
        const current = firstForecastRecordByGroup[groupKey];
        if (!current || compareTimeUnits(record[timeUnitIdx], current[timeUnitIdx]) < 0) {
            firstForecastRecordByGroup[groupKey] = record;
        }
    }

    const missingRecords = Object.values(firstForecastRecordByGroup)
        .map((forecastStartRecord) => {
            const firstForecastTimeUnit = forecastStartRecord[timeUnitIdx];
            const lineRecords =
                pastEstimateRecordsByLine[getHistoricalLineKey(forecastStartRecord)] || [];

            // Bridge from the latest historical point at or before this line's
            // forecast start, so the historical and forecast lines share that point.
            // `<=` keeps the boundary-year point when historical and forecast data
            // overlap there (e.g. Sequela historical and forecast both at 2025),
            // while still selecting 2025 for Cause whose forecast starts in 2026.
            const correspondingPastEstimateRecord = lineRecords
                .filter(
                    (record) => compareTimeUnits(record[timeUnitIdx], firstForecastTimeUnit) <= 0
                )
                .reduce<DataRecord | null>(
                    (latest, current) =>
                        !latest || compareTimeUnits(current[timeUnitIdx], latest[timeUnitIdx]) > 0
                            ? current
                            : latest,
                    null
                );

            if (!correspondingPastEstimateRecord) {
                return null;
            }

            const correspondingPastEstimateRecordCopy = [...correspondingPastEstimateRecord];

            if (
                forecastStartRecord[upperIdx] === forecastStartRecord[lowerIdx] &&
                forecastStartRecord[upperIdx] === null
            ) {
                correspondingPastEstimateRecordCopy[upperIdx] = null;
                correspondingPastEstimateRecordCopy[lowerIdx] = null;
            }

            correspondingPastEstimateRecordCopy[forecastScenarioIdx] =
                forecastStartRecord[forecastScenarioIdx];
            return correspondingPastEstimateRecordCopy;
        })
        .filter((record): record is DataRecord => record !== null);

    const dataResponseWithMissingRecords = {
        columns,
        records: [...records, ...missingRecords],
    };

    return sortDataResponseByTimeUnit(dataResponseWithMissingRecords, timeUnitKey);
};

export const localizeColumn = (
    column: DataKey,
    dataTypes,
    _: (key: string) => string,
    replaceSpacesInColumnNames?: boolean
): string => {
    const isPrimaryEntityKey = column === PRIMARY_ENTITY_ID_LOCALIZATION_KEY;
    const isComorbidityData = areComorbidityDataTypes(dataTypes);
    const isAMRData = dataTypes.includes(AntimicrobialResistanceRecords);

    let localizationKey = column;
    if (isPrimaryEntityKey) {
        if (isComorbidityData) {
            localizationKey = UNDERLYING_CONDITION_KEY;
        } else if (isAMRData) {
            localizationKey = PATHOGEN_ID_KEY;
        }
    }

    const columnName = _(`filter_label_${localizationKey}`);
    if (!replaceSpacesInColumnNames) {
        return columnName;
    }
    return columnName.replace(/\s+/g, '_');
};

export const localizeColumns = (
    columns: Array<DataKey>,
    dataTypes,
    _: (key: string) => string,
    replaceSpaces?: boolean
): string[] => columns.map((column) => localizeColumn(column, dataTypes, _, replaceSpaces));

export const localizeRecords = (
    columns: Array<DataKey | typeof DATA_TYPE_KEY>,
    records: DataRecord,
    // @todo: try to import localizer
    _: (key: string) => string
): DataRecord[] => {
    if (!(columns && columns.length && records && records.length)) {
        return [];
    }
    return records.map((record: DataRecord) => localizeRecord(record, columns));
};

export const localizeRecord = (
    record: DataRecord,
    columns: Array<DataKey | typeof DATA_TYPE_KEY>
): DataRecord => {
    const dataTypeIdx = columns.indexOf(DATA_TYPE_KEY);
    const dataType = record[dataTypeIdx];

    return record.map((value, idx) =>
        localizeRecordValue(value, columns[idx], dataType)
    ) as DataRecord;
};

export const localizeRecordValue = (
    value: string | number,
    granularityKey: string,
    dataType: DataType
) => {
    return formatValue(granularityKey, value, _, { metric_id: 'long_metric_' }, dataType);
};

export const mergeAndSortDataResponses = (
    data: Record<string, DataResponse> | null
): DataResponse => {
    if (!data) {
        return { columns: [], records: [] };
    }

    const mergedDataResponses = mergeDataResponses(data);

    const timeUnitKeys = intersection(
        getTimeUnitKeys(),
        mergedDataResponses.columns
    ) as TimeUnitKey[];

    if (isEmpty(timeUnitKeys)) {
        return mergedDataResponses;
    }

    return sortDataResponseByTimeUnit(mergedDataResponses, timeUnitKeys[0]);
};

export const mergeDataResponses = (data: Record<string, DataResponse> | null): DataResponse => {
    if (!data) {
        return { columns: [], records: [] };
    }

    const responsesWithDataTypes = addDataTypeToResponses(data);
    const columns = mergeDataColumns(responsesWithDataTypes);
    const records = mergeDataRecords(responsesWithDataTypes, columns);

    return {
        columns,
        records,
    };
};

// @deprecated
export const getFirstSupportedDataResponse = (
    data: Record<string, DataResponse>,
    chartType?: ChartType
): DataResponse => {
    const emptyResponse = { columns: [], records: [] };
    if (!data) {
        return emptyResponse;
    }
    const responses = Object.entries(data);
    if (!responses?.length) {
        return emptyResponse;
    }

    const supportedDataResponses = responses
        .filter(
            ([dataType]) =>
                !chartType || !(CHARTS_NON_SUPPORTED_DATA_TYPES[chartType] || []).includes(dataType)
        )
        .map(([dataType, dataResponse]) => dataResponse);

    return supportedDataResponses?.[0] || emptyResponse;
};

export const addDataTypeToResponses = (data: Record<string, DataResponse>) => {
    if (!data) {
        return null;
    }

    const responses = Object.entries(data);

    const responsesWithDataType = responses.map(([key, value]) => [
        key,
        addDataTypeToResponse(value, key),
    ]);

    return fromPairs(responsesWithDataType);
};

export const addDataTypeToResponse = (response: DataResponse, dataType: DataType) => {
    const { columns, records } = response;

    const columnsWithDataType = addDataTypeToColumns(columns, dataType);
    const recordsWithDataType = addDataTypeToRecords(columns, records, dataType);

    return {
        columns: columnsWithDataType,
        records: recordsWithDataType,
    };
};

const addDataTypeToColumns = (columns: ReadonlyArray<DataKey>, dataType: DataType) =>
    [PRIMARY_ENTITY_ID_LOCALIZATION_KEY, columns[0], DATA_TYPE_KEY, ...columns.slice(1)].map(
        (filterKey) =>
            filterKey === getDataTypePrimaryEntityKey(dataType) ? PRIMARY_ENTITY_ID_KEY : filterKey
    );

const addDataTypeToRecords = (
    columns: DeepReadonly<
        (
            | DataKey
            | typeof PRIMARY_ENTITY_ID_KEY
            | typeof DATA_TYPE_KEY
            | typeof PRIMARY_ENTITY_ID_LOCALIZATION_KEY
        )[]
    >,
    records: DeepReadonly<DataRecord[]>,
    dataType: DataType
) => {
    const primaryEntityIdIdx = columns.indexOf(getDataTypePrimaryEntityKey(dataType));
    const hasPrimaryEntity = dataTypeHasPrimaryEntity(dataType);
    const localePrefix = getDataTypeLocalePrefix(dataType);

    return records.map((record: DeepReadonly<DataRecord>) => [
        `${localePrefix}${hasPrimaryEntity ? record[primaryEntityIdIdx] : 1}`,
        record[0],
        dataType,
        ...record.slice(1),
    ]);
};

export const mergeDataColumns = (data: Record<string, DataResponse>): DataKey[] => {
    const responses = Object.entries(data).filter(([, res]) => res?.columns);
    if (isEmpty(responses)) {
        return [];
    }

    // Preserve order: use first response's columns, then append any extra from others
    const firstColumns = responses[0][1].columns ?? [];

    const responsesAmount = responses.length;
    if (responsesAmount === 1) {
        return firstColumns;
    }

    // Check if columns of multiple responses are compatible (same columns except primary entity key)
    const incompatible = responses.some(([type, res], idx) => {
        const columns = res.columns.filter(
            (i) => i !== getDataTypePrimaryEntityKey(type as DataType)
        );
        return responses.some(([_type, _res], _idx) => {
            if (_idx === idx) return false;
            const otherColumns = _res.columns.filter(
                (i) => i !== getDataTypePrimaryEntityKey(_type as DataType)
            );
            return !isEmpty(difference(columns, otherColumns));
        });
    });

    if (incompatible) {
        throw new Error('Columns are different when merging data responses');
    }

    const seen = new Set(firstColumns);
    for (let i = 1; i < responsesAmount; i++) {
        const cols = responses[i][1].columns ?? [];
        for (const col of cols) {
            if (!seen.has(col)) {
                seen.add(col);
                firstColumns.push(col);
            }
        }
    }
    return firstColumns;
};

export const mergeDataRecords = (data: Record<string, DataResponse>, mergedColumns: DataKey[]) => {
    const allRecords: DataRecord[] = [];

    Object.values(data).forEach((response) => {
        if (!response?.columns || !response?.records) {
            return;
        }

        const { columns, records } = response;
        const indexMap = mergedColumns.map((col) => columns.indexOf(col));

        records.forEach((record) => {
            const mergedRecord = indexMap.map((idx) => (idx === -1 ? null : record[idx]));
            allRecords.push(mergedRecord);
        });
    });

    return allRecords;
};

// @todo: delete
export const localizeConditions = (conditions: DataCondition[]) =>
    conditions.map(localizeCondition).join(', ');

// @todo: delete
export const localizeCondition = (condition: DataCondition) =>
    _(getDataTypeLocalePrefix(condition.data_type) + condition.primary_entity_id);

export const mapConditionToValue = (condition: DataCondition): string =>
    condition.data_type + '::' + condition.primary_entity_id;

export const mapValueToCondition = (value: string): DataCondition => {
    const [data_type, id] = value.split('::');
    return { data_type, primary_entity_id: parseInt(id) };
};

export const localizeConditionValue = (value: string): string =>
    localizeCondition(mapValueToCondition(value));

const filterLocalePrefixMap = {
    [AGE_GROUP_ID_KEY]: 'age_group_',
    [CAUSE_ID_KEY]: 'cause_',
    [COVARIATE_ID_KEY]: 'covariate_',
    [SDOH_ID_KEY]: 'sdoh_',
    [FORECAST_SCENARIO_ID_KEY]: 'forecast_scenario_',
    [GENDER_ID_KEY]: 'full_gender_',
    [LOCATION_ID_KEY]: 'location_',
    [MEASURE_ID_KEY]: 'measure_',
    [METRIC_ID_KEY]: 'long_metric_',
    [REI_ID_KEY]: 'rei_',
    [RISK_EXPOSURE_ID_KEY]: 'risk_exposure_',
    [ROUND_ID_KEY]: 'round_',
    [SEQUELA_ID_KEY]: 'sequela_',
    [COMORBID_CAUSE_ID_KEY]: 'cause_',
    [RACE_ID_KEY]: 'race_',
    [ANTIBIOTIC_CLASS_ID_KEY]: 'antibiotic_class_',
    [PATHOGEN_ID_KEY]: 'pathogen_',
    [INFECTIOUS_SYNDROME_ID_KEY]: 'infectious_syndrome_',
    [COUNTERFACTUAL_ID_KEY]: 'counterfactual_',
    [PRIMARY_ENTITY_ID_LOCALIZATION_KEY]: '',
    [RESPONSE_ID_KEY]: 'response_',
    [EDUCATION_ID_KEY]: 'education_',
    [SEVERITY_ID_KEY]: 'severity_',
    [OUTCOME_ID_KEY]: 'outcome_',
    [INFECTION_TYPE_ID_KEY]: 'infection_type_',
    [VACCINATION_STATUS_ID_KEY]: 'vaccination_status_',
    [PAYER_ID_KEY]: 'payer_',
    [TOC_ID_KEY]: 'toc_',
    [APPARENT_ID_KEY]: 'apparent_',
    [SYMPTOMATIC_ID_KEY]: 'symptomatic_',
    [CRO_ID_KEY]: 'cro_',
};

// TODO: it's not clear why below was added, what was the requirement
const getFilterLocalePrefixMap = (dataType: DataType | ''): object => {
    const baseMap = filterLocalePrefixMap;
    const dataTypeSpecificMap =
        dataType === SevRecords
            ? {
                  [METRIC_ID_KEY]: 'sev_records_long_metric_',
              }
            : dataType === HealthThreatsMetricsRecords
              ? {
                    [MEASURE_ID_KEY]: 'ght_records_measure_',
                }
              : {};

    return {
        ...baseMap,
        ...dataTypeSpecificMap,
    };
};

export const localizeFilterValues = (
    filter: string,
    values: number[],
    dataType: DataType | '' = ''
): string => {
    const prefixMap = getFilterLocalePrefixMap(dataType);
    const prefix = prefixMap[filter];
    return (values || []).map((value) => _(`${prefix}${value}`)).join(', ');
};

export const getChartTitle = (
    conditionTypes: DataType[],
    localizedSelectedConditions: string, // @todo Better API would be selectionConditionsGranularity {cause_id: [...], sequela_id: [...]}
    selectedFilterValues: DataGranularity,
    granularityFiltersAvailableForSelectedConditions: DataGranularityKey[] | string[],
    titleFilters: DataGranularityKey[],
    timelineValue: string | number,
    chartType?: ChartType
): string => {
    const filters = intersection(
        titleFilters,
        Object.keys(selectedFilterValues),
        granularityFiltersAvailableForSelectedConditions
    );

    const isCovid19Data = conditionTypes?.includes(Covid19Records);
    const isRisksExposureData = conditionTypes?.includes(RiskExposureRecords);
    const isLifeExpectancyData = conditionTypes?.includes(LifeExpectancyRecords);
    const isDengueData = conditionTypes?.includes(TakedaDengueRecords);
    const isComorbidityData = areComorbidityDataTypes(conditionTypes);
    const isGHTData = conditionTypes?.includes(HealthThreatsMetricsRecords);

    let titleParts: string[] = [];

    // For Covid-19 data unit should be first
    if (isCovid19Data && filters?.includes(METRIC_ID_KEY) && selectedFilterValues[METRIC_ID_KEY]) {
        titleParts.push(
            localizeFilterValues(
                METRIC_ID_KEY,
                selectedFilterValues[METRIC_ID_KEY],
                conditionTypes[0]
            )
        );
        pull(filters, METRIC_ID_KEY);
    }

    // If we have conditions
    if (localizedSelectedConditions) {
        // If we display REI data types
        if (areReiDataTypes(conditionTypes)) {
            const hasCauses = !isEmpty(selectedFilterValues[CAUSE_ID_KEY]);
            if (hasCauses) {
                titleParts.push(
                    localizeFilterValues(
                        CAUSE_ID_KEY,
                        selectedFilterValues[CAUSE_ID_KEY],
                        conditionTypes[0]
                    )
                );
                titleParts.push('due to');
            }
            titleParts.push(localizedSelectedConditions);
            if (!isEmpty(intersection(conditionTypes, [ImpairmentRecords, InjuryRecords]))) {
                titleParts.reverse();
            }
            pull(filters, CAUSE_ID_KEY);
        }
        // If we have conditions Risk Exposure
        else if (isRisksExposureData) {
            titleParts.push(
                localizeFilterValues(
                    RISK_EXPOSURE_ID_KEY,
                    selectedFilterValues[RISK_EXPOSURE_ID_KEY],
                    conditionTypes[0]
                )
            );
            pull(filters, RISK_EXPOSURE_ID_KEY);
        } else if (!isLifeExpectancyData) {
            titleParts.push(localizedSelectedConditions);
        }
    }
    // If we have only data types
    else {
        const localizedSelectedTypes = (conditionTypes || [])
            .map((dataType) => _(`data_type_${dataType}`))
            .join(', ');

        // If we display REI data types
        if (areReiDataTypes(conditionTypes)) {
            titleParts.push(localizedSelectedTypes);
            titleParts.push('map for');
            titleParts.push(
                localizeFilterValues(
                    CAUSE_ID_KEY,
                    selectedFilterValues[CAUSE_ID_KEY],
                    conditionTypes[0]
                )
            );
            pull(filters, CAUSE_ID_KEY);
        } else {
            titleParts.push(localizedSelectedTypes);
        }
    }

    // Remove condition primary entity ids from the rest of the filters
    for (const dataType of conditionTypes) {
        if (dataTypeHasPrimaryEntity(dataType)) {
            pull(filters, getDataTypePrimaryEntityKey(dataType));
        }
    }

    // CRO chart title should not include cause
    const filtersForTitle =
        conditionTypes?.includes(CroRecords) && filters?.length
            ? filters.filter((filter) => filter !== CAUSE_ID_KEY)
            : filters;

    for (const filter of filtersForTitle) {
        if (
            filter === MEASURE_ID_KEY &&
            selectedFilterValues[filter].includes(populationMeasureId) &&
            conditionTypes.includes(PopulationRecords)
        ) {
            continue;
        }
        titleParts.push(
            localizeFilterValues(filter, selectedFilterValues[filter], conditionTypes[0])
        );
    }

    if (isDengueData) {
        titleParts = [];
        titleParts.push(`${_(TakedaDengueRecords)},`);
        const statementOutcomeString = getDengueStatementOutcomeString(selectedFilterValues);

        if (statementOutcomeString) {
            titleParts.push(statementOutcomeString);
        }

        if (filters.includes(METRIC_ID_KEY)) {
            titleParts.push(
                localizeFilterValues(METRIC_ID_KEY, selectedFilterValues[METRIC_ID_KEY])
            );
        }
    }

    titleParts.push('in');
    let dateRange = timelineValue.toString();
    if (isDengueData) {
        titleParts.push('Year');
        dateRange = dateRange.replace(' to ', ' - ');
    }
    titleParts.push(dateRange);

    // Comorbidity tool
    if (isComorbidityData) {
        titleParts = [];
        const comorbidCauseName = localizeFilterValues(
            COMORBID_CAUSE_ID_KEY,
            selectedFilterValues[COMORBID_CAUSE_ID_KEY],
            conditionTypes[0]
        );

        if (areComorbidityCoprevalenceDataTypes(conditionTypes)) {
            const metricId = selectedFilterValues[METRIC_ID_KEY][0];
            if (metricId === 1) {
                // Number unit
                titleParts.push(
                    `Number of prevalent cases of ${comorbidCauseName} among individuals with ${localizedSelectedConditions} in ${timelineValue}`
                );
            } else if (metricId === 2) {
                // Number unit
                titleParts.push(
                    `Prevalence (%) of ${comorbidCauseName} among individuals with ${localizedSelectedConditions} in ${timelineValue}`
                );
            } else {
                // Rate unit
                titleParts.push(
                    `Prevalence of ${comorbidCauseName} per 100,000 cases of ${localizedSelectedConditions} in ${timelineValue}`
                );
            }
        }
        // projected_comorbidity_causes_outcome_records data type
        else {
            titleParts.push(
                `Projected new cases of ${comorbidCauseName} per 100,000 cases of ${localizedSelectedConditions} over a 12-month period`
            );
        }
    }

    if (isGHTData && chartType === TYPE_SCATTER_MAP_CHART) {
        titleParts = [`Highest threat by location as of ${timelineValue}`];
    }

    return capitalize(titleParts.filter((v) => v !== '').join(' '));
};

export const localizeDengueFilterValue = (filter: DataKey, filtersValues: DataFilters) =>
    localizeFilterValues(filter, filtersValues[filter], TakedaDengueRecords);

export const getDengueStatementOutcomeString = (
    filtersValues: DataFilters,
    anyOutcomeId: number = 4,
    deathOutcomeId: number = 3,
    anyApparentId: number = 3,
    anySymptomaticId: number = 3
) => {
    let result = '';
    const apparentId = filtersValues[APPARENT_ID_KEY]?.[0];
    const symptomaticId = filtersValues[SYMPTOMATIC_ID_KEY]?.[0];
    const outcomeId = filtersValues[OUTCOME_ID_KEY]?.[0];

    if (apparentId !== anyApparentId) {
        result += localizeDengueFilterValue(APPARENT_ID_KEY, filtersValues) + ', ';
    }

    if (symptomaticId !== anySymptomaticId) {
        result += localizeDengueFilterValue(SYMPTOMATIC_ID_KEY, filtersValues) + ' ';
    }

    if (outcomeId === deathOutcomeId) {
        result += _('dengue_averted_outcome_title_death_outcome');
    } else if (outcomeId === anyOutcomeId) {
        result += _('dengue_averted_outcome_title_other_outcome');
    } else {
        result +=
            localizeDengueFilterValue(OUTCOME_ID_KEY, filtersValues) +
            ' ' +
            _('dengue_averted_outcome_title_other_outcome');
    }

    return result;
};

const capitalize = (value: string): string => value[0].toUpperCase() + value.slice(1);

export const getChartSubTitle = (
    selectedFilterValues: DataGranularity,
    granularityFiltersAvailableForSelectedConditions: DataGranularityKey[] | string[],
    titleFilters: DataGranularityKey[],
    dataTypes: DataType[] | null = null,
    ageGroupEntities: AgeGroupEntity[] | null = null
): string => {
    let filters;

    if (dataTypes && dataTypes.includes(TakedaDengueRecords)) {
        const dengueSubtitleOrder = [
            SEVERITY_ID_KEY,
            INFECTION_TYPE_ID_KEY,
            VACCINATION_STATUS_ID_KEY,
            AGE_GROUP_ID_KEY,
            LOCATION_ID_KEY,
            GENDER_ID_KEY,
        ];

        const availableFilters = intersection(
            dengueSubtitleOrder,
            Object.keys(selectedFilterValues),
            granularityFiltersAvailableForSelectedConditions,
            titleFilters
        );

        const vaccinationStatusIndex = availableFilters.indexOf(VACCINATION_STATUS_ID_KEY);
        const beforeIn = availableFilters.slice(0, vaccinationStatusIndex);
        const afterIn = availableFilters.slice(vaccinationStatusIndex);

        const formatFilter = (filter) => {
            if (filter === AGE_GROUP_ID_KEY && ageGroupEntities) {
                return getAgeGroupsRange(selectedFilterValues[filter], ageGroupEntities);
            }
            if (
                filter === VACCINATION_STATUS_ID_KEY &&
                selectedFilterValues[filter]?.[0] === anyVaccinationStatusId
            ) {
                return _('dengue_scenarios_any_vaccination_status');
            }
            const text = localizeFilterValues(
                filter,
                selectedFilterValues[filter],
                TakedaDengueRecords
            );
            if (filter === SEVERITY_ID_KEY) {
                return `${text} ${_('dengue_scenarios_severity')}`;
            }
            if (filter === INFECTION_TYPE_ID_KEY) {
                return `${text} ${_('dengue_scenarios_infection')}`;
            }
            return text;
        };

        const beforeInFormatted = beforeIn.map(formatFilter);

        const parts = [
            beforeInFormatted.join(', '),
            afterIn.length > 0 && beforeInFormatted.length > 0 ? 'in' : '',
            afterIn.map(formatFilter).join(', '),
        ].filter(Boolean);

        return parts.join(' ');
    }

    if (dataTypes && areComorbidityDataTypes(dataTypes) && ageGroupEntities) {
        // Comorbidity tool
        filters = [LOCATION_ID_KEY, GENDER_ID_KEY];
        if (dataTypes.includes(CauseAssociatedComorbidityStudyCoefficients)) {
            filters.push(AGE_GROUP_ID_KEY);
        }

        return filters
            .map((filter) =>
                filter === AGE_GROUP_ID_KEY
                    ? getAgeGroupsRange(selectedFilterValues[filter], ageGroupEntities)
                    : localizeFilterValues(filter, selectedFilterValues[filter])
            )
            .join(', ');
    }

    filters =
        intersection(
            titleFilters,
            Object.keys(selectedFilterValues),
            granularityFiltersAvailableForSelectedConditions
        ) || [];

    return filters
        .map((filter) => localizeFilterValues(filter, selectedFilterValues[filter]))
        .join(', ');
};

export const getYAxisTitle = (
    selectedFilterValues: DataGranularity,
    dataType: DataType
): string => {
    if (selectedFilterValues[METRIC_ID_KEY]) {
        return localizeFilterValues(METRIC_ID_KEY, selectedFilterValues[METRIC_ID_KEY], dataType);
    }

    if (selectedFilterValues[MEASURE_ID_KEY]) {
        return localizeFilterValues(MEASURE_ID_KEY, selectedFilterValues[MEASURE_ID_KEY], dataType);
    }

    return '';
};

export const chartProviderFilterValueToArray = (value: ChartProviderFilterValue): number[] =>
    value
        .toString()
        .split(',')
        .map((v) => parseInt(v));

export const chartProviderFiltersToObject = (
    chartProviderFilters: ChartProviderFilters
): object => {
    const result = {};

    for (const filter in chartProviderFilters) {
        if (chartProviderFilters[filter]) {
            result[filter] = chartProviderFilterValueToArray(chartProviderFilters[filter]).map(
                (id) => parseInt(id)
            );
        }
    }

    return result;
};

export const getLeadingDataTypeInMultiTypeSelections = (dataTypes: DataType[]): DataType => {
    if (
        dataTypes.includes(CauseOutcome) &&
        (dataTypes.includes(SequelaOutcome) || dataTypes.includes(CroRecords))
    ) {
        return CauseOutcome;
    }

    return dataTypes[0];
};
